import React from 'react';
import { Gift, XCircle, CreditCard, Plane, HeadphonesIcon, Shield, CheckCircle2 } from 'lucide-react';

/**
 * SEO Benefits Section
 * 
 * This component renders benefits as crawlable HTML text for SEO purposes.
 * All content is visible to search engines in the DOM, not hidden in JS/graphics.
 */
const SeoBenefitsSection = () => {
  const benefits = [
    {
      icon: Gift,
      title: 'Complimentary Meet & Greet Included',
      description: 'Our professional driver will meet you at the airport arrivals hall with a personalized name board. This €15 value service is included FREE with every booking.',
      highlight: true,
    },
    {
      icon: XCircle,
      title: 'Free Cancellation Available',
      description: 'Plans change - we understand. Cancel your booking up to 24 hours before your scheduled pickup time and receive a full refund. No questions asked.',
      highlight: true,
    },
    {
      icon: CreditCard,
      title: 'Fixed Pricing Guaranteed',
      description: 'The price you see is the price you pay. No surge pricing, no hidden fees, no surprises. All tolls, fuel, and waiting time are included in your quoted price.',
      highlight: false,
    },
    {
      icon: Plane,
      title: 'Real-Time Flight Monitoring',
      description: 'We track your flight in real-time. If your flight is delayed or arrives early, your driver will adjust accordingly at no extra charge.',
      highlight: false,
    },
    {
      icon: HeadphonesIcon,
      title: '24/7 Customer Support',
      description: 'Our support team is available around the clock via WhatsApp, email, or phone. Get help whenever you need it, day or night.',
      highlight: false,
    },
    {
      icon: Shield,
      title: 'Licensed Professional Drivers',
      description: 'All our drivers are fully licensed, vetted, and experienced. They speak English and are trained to meet international service standards.',
      highlight: false,
    },
  ];

  return (
    <section className="py-16 lg:py-24 bg-secondary" aria-labelledby="benefits-heading">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* SEO-friendly heading */}
          <header className="text-center mb-12">
            <h2 id="benefits-heading" className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Choose GoTurkeyTransfer for Your Airport Transfer?
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Experience premium VIP airport transfer service in Turkey with complimentary Meet & Greet, 
              free cancellation, and fixed pricing. Book with confidence.
            </p>
          </header>

          {/* Benefits grid with semantic HTML */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" role="list">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <article 
                  key={index}
                  className={`rounded-2xl p-6 ${
                    benefit.highlight 
                      ? 'bg-green-500/10 border-2 border-green-500/30' 
                      : 'bg-black/50 border border-white/10'
                  }`}
                  role="listitem"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    benefit.highlight ? 'bg-green-500/20' : 'bg-orange-500/10'
                  }`}>
                    <Icon className={`h-6 w-6 ${benefit.highlight ? 'text-green-400' : 'text-orange-500'}`} />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${benefit.highlight ? 'text-green-400' : 'text-white'}`}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </article>
              );
            })}
          </div>

          {/* Additional SEO text content */}
          <div className="mt-12 text-center">
            <div className="inline-flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span>Free Meet & Greet service included</span>
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span>Free cancellation up to 24 hours</span>
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-orange-500" />
                <span>Fixed prices with no hidden fees</span>
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-orange-500" />
                <span>Real-time flight tracking</span>
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-orange-500" />
                <span>24/7 customer support available</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeoBenefitsSection;
